import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@artosera/ui';
import {
  aplatirEmplacements,
  reserveApi,
  useCaisses,
  useCampagnes,
  useEmplacements,
  useMutationReserve,
  type PerimetreInventaire,
} from '../../api/reserve.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useLieux } from '../../api/lieux.js';
import { Field } from '../Field.js';
import styles from './InventairePage.module.css';

export function CampagnesPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const navigate = useNavigate();
  const liste = useCampagnes(galerieSlug);
  const lieux = useLieux(galerieSlug);
  const emplacements = useEmplacements(galerieSlug);
  const caisses = useCaisses(galerieSlug, { parPage: 200 });
  const [type, setType] = useState<PerimetreInventaire['type']>('lieu');
  const [lieuId, setLieuId] = useState('');
  const [emplacementId, setEmplacementId] = useState('');
  const [caisseIds, setCaisseIds] = useState<string[]>([]);
  const ouvrir = useMutationReserve(galerieSlug, (perimetre: PerimetreInventaire, appel) =>
    reserveApi.ouvrirCampagne(galerieSlug, perimetre, appel),
  );
  let perimetre: PerimetreInventaire | null = null;
  if (type === 'lieu' && lieuId) perimetre = { type, lieuId };
  if (type === 'emplacement' && emplacementId) perimetre = { type, emplacementId };
  if (type === 'caisses' && caisseIds.length) perimetre = { type, caisseIds };
  return (
    <div className={styles.page}>
      <h2>{t('regie:inventaire.campagnes')}</h2>
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      <div className={styles.liste}>
        {liste.data?.campagnes.map((c) => (
          <Link
            className={styles.ligne}
            key={c.id}
            to={`/${galerieSlug}/inventaire/campagnes/${c.id}`}
          >
            <strong>{c.numero}</strong>
            <span>{t(`regie:inventaire.etatsCampagne.${c.etat}`)}</span>
          </Link>
        ))}
      </div>
      {galerieALaction(galerie, 'inventaire:write') && (
        <section className={styles.panneau}>
          <h3>{t('regie:inventaire.ouvrirCampagne')}</h3>
          <div className={styles.formulaire}>
            <Field htmlFor="campagne-type" label={t('regie:inventaire.perimetre')}>
              <Select
                id="campagne-type"
                value={type}
                onValueChange={(v) => setType(v as PerimetreInventaire['type'])}
                options={['lieu', 'emplacement', 'caisses'].map((v) => ({
                  value: v,
                  label: t(`regie:inventaire.${v}`),
                }))}
              />
            </Field>
            {type === 'lieu' && (
              <Field htmlFor="campagne-lieu" label={t('regie:inventaire.lieu')}>
                <Select
                  id="campagne-lieu"
                  value={lieuId || 'aucun'}
                  onValueChange={(v) => setLieuId(v === 'aucun' ? '' : v)}
                  options={[
                    { value: 'aucun', label: t('regie:inventaire.choisir') },
                    ...(lieux.data ?? [])
                      .filter((l) => l.actif)
                      .map((l) => ({ value: l.id, label: l.nom })),
                  ]}
                />
              </Field>
            )}
            {type === 'emplacement' && (
              <Field htmlFor="campagne-emplacement" label={t('regie:inventaire.emplacement')}>
                <Select
                  id="campagne-emplacement"
                  value={emplacementId || 'aucun'}
                  onValueChange={(v) => setEmplacementId(v === 'aucun' ? '' : v)}
                  options={[
                    { value: 'aucun', label: t('regie:inventaire.choisir') },
                    ...aplatirEmplacements(emplacements.data?.emplacements ?? [])
                      .filter((e) => e.actif)
                      .map((e) => ({ value: e.id, label: e.chemin })),
                  ]}
                />
              </Field>
            )}
            {type === 'caisses' && (
              <fieldset>
                <legend>{t('regie:inventaire.caisses')}</legend>
                {caisses.data?.caisses.map((c) => (
                  <label className={styles.case} key={c.id}>
                    <input
                      type="checkbox"
                      checked={caisseIds.includes(c.id)}
                      onChange={(e) =>
                        setCaisseIds(
                          e.target.checked
                            ? [...caisseIds, c.id]
                            : caisseIds.filter((id) => id !== c.id),
                        )
                      }
                    />
                    {c.numero}
                  </label>
                ))}
              </fieldset>
            )}
            <Button
              disabled={!perimetre || ouvrir.isPending}
              onClick={() => {
                if (perimetre)
                  ouvrir.mutate(perimetre, {
                    onSuccess: ({ campagne }) =>
                      navigate(`/${galerieSlug}/inventaire/campagnes/${campagne.id}`),
                  });
              }}
            >
              {t('regie:inventaire.ouvrirCampagne')}
            </Button>
            {ouvrir.error && (
              <p role="alert">
                {estApiError(ouvrir.error) ? ouvrir.error.message : t('commun:erreurReseau')}
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
